/**
 * JulietteTextPanel — Mode "ecrire" de la surface Juliette unifiee.
 *
 * Chat texte complet, SILENCIEUX (aucun TTS) : pour les contextes ou l'agent ne
 * peut/veut pas parler (RDV en agence, lieu public, mains prises). Branche sur le
 * MEME backend que la voix : un tour = { session_id, user_message } -> turn-process.
 * Les reponses arrivent via la subscription realtime sur juliette_messages, donc
 * les tours vocaux passes de la meme session s'affichent aussi (source de verite).
 *
 * Pense pour le fond sombre immersif de JulietteFullscreen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUp, Loader2, Paperclip, X } from "lucide-react";
import { AddToCalendarButton } from "./AddToCalendarButton";
import { GoToPageButton } from "./GoToPageButton";
import { extractAddCal } from "@/lib/juliette-addcal";
import { extractAllGotos } from "@/lib/juliette-goto";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useJulietteLauncher } from "@/hooks/use-juliette-launcher";
import { maybeAutoTitleThread } from "@/lib/juliette-session";
import { JulietteOrb } from "@/components/juliette/JulietteOrb";
import { MessageFeedback } from "@/components/juliette/MessageFeedback";
import { prepareImageForJuliette, ImageError, ACCEPTED_IMAGE_TYPES } from "@/lib/image-utils";

interface MessageRow {
  id: string;
  role: string;
  content: string | null;
  created_at: string;
}

interface JulietteTextPanelProps {
  sessionId: string;
  teamId: string;
  /** Notifie le shell quand l'agent envoie son 1er message (pour le titre/refresh historique). */
  onUserSent?: () => void;
  /**
   * Le serveur a route le tour vers un AUTRE fil (fils par client) : le shell
   * doit basculer sessionId pour que l'onglet actif et les messages suivent.
   */
  onSessionRouted?: (sessionId: string) => void;
  /**
   * Briefing dashboard a jouer a l'ouverture : la file d'actions EXACTE affichee
   * a l'ecran. Juliette prend la parole d'elle-meme au lieu d'attendre.
   */
  briefingDeck?: string | null;
  /** Le briefing a ete lance : le shell peut le desarmer (une seule fois). */
  onBriefingConsumed?: () => void;
}

export function JulietteTextPanel({
  sessionId,
  teamId,
  onUserSent,
  onSessionRouted,
  briefingDeck,
  onBriefingConsumed,
}: JulietteTextPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { prefill, openTick } = useJulietteLauncher();
  // Route courante transmise au backend pour que Juliette sache sur quelle page est l'agent.
  const { pathname } = useLocation();
  const [draft, setDraft] = useState("");
  const [turnLoading, setTurnLoading] = useState(false);
  // Capture d'écran à envoyer ce tour-ci. Éphémère : non stockée côté serveur.
  const [image, setImage] = useState<{ dataUrl: string; mimeType: string; sizeBytes: number } | null>(null);
  const scrollEndRef = useRef<HTMLDivElement | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const { data: messages = [] } = useQuery({
    queryKey: ["juliette-messages", sessionId],
    enabled: !!sessionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("juliette_messages")
        .select("id, role, content, created_at")
        .eq("session_id", sessionId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as MessageRow[];
    },
  });

  // Realtime : tout nouveau message (user OU assistant) rafraichit la liste.
  useEffect(() => {
    if (!sessionId) return;
    const channel = supabase
      .channel(`juliette-text-${sessionId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "juliette_messages", filter: `session_id=eq.${sessionId}` },
        () => void queryClient.invalidateQueries({ queryKey: ["juliette-messages", sessionId] }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [sessionId, queryClient]);

  // Briefing dashboard : Juliette ouvre la conversation en deroulant la file
  // d'actions affichee (premier tour sans message de l'agent). Une seule fois,
  // et seulement sur un fil vierge (jamais par-dessus un echange existant).
  const briefingFiredRef = useRef(false);
  useEffect(() => {
    if (!briefingDeck || !sessionId || briefingFiredRef.current) return;
    briefingFiredRef.current = true;
    onBriefingConsumed?.();
    setTurnLoading(true);
    void (async () => {
      try {
        const { error } = await supabase.functions.invoke("juliette-turn-process", {
          body: {
            session_id: sessionId,
            user_message: "",
            is_first_turn: true,
            supports_thread_routing: true,
            client_context: { current_route: pathname, dashboard_deck: briefingDeck },
          },
        });
        if (error) throw error;
      } catch (err) {
        toast({
          title: "Briefing indisponible",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
      } finally {
        setTurnLoading(false);
        void queryClient.invalidateQueries({ queryKey: ["juliette-messages", sessionId] });
      }
    })();
  }, [briefingDeck, sessionId, onBriefingConsumed, pathname, toast, queryClient]);

  // On masque les commandes structurees [ACTION:...] (plomberie des boutons).
  const visibleMessages = useMemo(
    () =>
      messages.filter(
        (m) => (m.role === "user" || m.role === "assistant") && m.content && !m.content.startsWith("[ACTION:"),
      ),
    [messages],
  );

  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [visibleMessages.length, turnLoading]);

  // Navigation guidee : quand Juliette termine un tour en posant [GOTO:/route],
  // on emmene l'agent sur la page.
  //
  // On ne navigue QUE sur la reponse a un message que l'agent vient d'envoyer
  // (drapeau arme dans send()). Sans ce drapeau, rouvrir une ancienne
  // conversation teleporterait l'agent sur la page de l'epoque. Le briefing
  // d'ouverture n'arme volontairement PAS le drapeau : l'agent vient de cliquer
  // depuis son tableau de bord, il doit y rester.
  //
  // Le panneau reste OUVERT : il est ancre a droite, la page se voit a cote.
  const navigate = useNavigate();
  const awaitingNavRef = useRef(false);
  const lastNavMsgRef = useRef<string | null>(null);
  useEffect(() => {
    if (!awaitingNavRef.current) return;
    const last = [...visibleMessages].reverse().find((m) => m.role === "assistant");
    if (!last || lastNavMsgRef.current === last.id) return;
    lastNavMsgRef.current = last.id;
    awaitingNavRef.current = false;
    // Plusieurs [GOTO:] dans une meme reponse (mode demo) : sans voix pour
    // donner le rythme, on saute directement a la DERNIERE route (destination
    // finale) plutot que d'enchainer des ecrans que personne ne commente.
    const { routes } = extractAllGotos(last.content ?? "");
    const route = routes[routes.length - 1];
    if (route && route !== pathname) navigate(route);
  }, [visibleMessages, navigate, pathname]);

  // Pre-remplissage depuis le launcher (ex : "Demander a Juliette" sur la fiche
  // contact). On ne reagit QU'au changement d'openTick (une ouverture) pour ne
  // pas ecraser un brouillon que l'agent est en train d'ecrire. Focus en fin.
  useEffect(() => {
    if (!prefill) return;
    setDraft(prefill);
    const ta = taRef.current;
    if (ta) {
      ta.focus();
      const end = prefill.length;
      ta.setSelectionRange(end, end);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTick]);

  // Prépare une image (fichier choisi ou capture collée) : valide + compresse,
  // puis la met en attente d'envoi. Erreur lisible via toast.
  const handleImageFile = useCallback(
    async (file: File | null | undefined) => {
      if (!file) return;
      try {
        const prepared = await prepareImageForJuliette(file);
        setImage(prepared);
      } catch (err) {
        toast({
          title: "Image non ajoutée",
          description: err instanceof ImageError ? err.message : "Impossible de préparer l'image.",
          variant: "destructive",
        });
      }
    },
    [toast],
  );

  const send = useCallback(async () => {
    const value = draft.trim();
    if ((!value && !image) || turnLoading) return;
    const sentImage = image;
    setDraft("");
    setImage(null);
    setTurnLoading(true);
    onUserSent?.();
    // Arme la navigation guidee : seule la reponse a CE message pourra deplacer
    // l'agent (cf. l'effet plus haut).
    awaitingNavRef.current = true;
    try {
      const { data, error } = await supabase.functions.invoke("juliette-turn-process", {
        body: {
          session_id: sessionId,
          user_message: value,
          image: sentImage
            ? { data_url: sentImage.dataUrl, mime_type: sentImage.mimeType, size_bytes: sentImage.sizeBytes }
            : null,
          is_first_turn: false,
          // Ce front sait suivre une bascule de fil (onglets par client).
          supports_thread_routing: true,
          client_context: { current_route: pathname },
        },
      });
      if (error) throw error;
      // Routage par contact : le serveur peut avoir fait le tour dans un autre
      // fil (celui du client cite). On titre le fil EFFECTIF (no-op si deja
      // titre — les fils clients naissent titres du nom du contact), puis on
      // fait suivre le shell pour que l'onglet actif bascule.
      const routedId = (data as { session_id?: string } | null)?.session_id;
      const effectiveId = routedId || sessionId;
      void maybeAutoTitleThread(effectiveId, value || "Capture d'écran");
      if (routedId && routedId !== sessionId) onSessionRouted?.(routedId);
    } catch (err) {
      toast({
        title: "Juliette est lente, retente dans un moment",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setTurnLoading(false);
      void queryClient.invalidateQueries({ queryKey: ["juliette-messages", sessionId] });
    }
  }, [draft, image, turnLoading, sessionId, onUserSent, onSessionRouted, toast, queryClient, pathname]);

  return (
    <div className="flex-1 w-full max-w-2xl mx-auto flex flex-col min-h-0">
      {/* Fil de discussion */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {visibleMessages.length === 0 && !turnLoading && (
          <div className="h-full flex flex-col items-center justify-center gap-4 text-center px-6">
            <JulietteOrb asAvatar size={96} state="idle" ariaLabel="Juliette" />
            <p className="font-juliette text-2xl text-white/90 leading-snug">
              Qu'est-ce qu'on regarde&nbsp;?
            </p>
            <p className="text-white/40 text-sm max-w-[260px]">
              Écris à Juliette — un contact, une relance, une note. Elle répond sans un bruit.
            </p>
          </div>
        )}

        {visibleMessages.map((m) => (
          <Bubble
            key={m.id}
            role={m.role}
            content={m.content ?? ""}
            feedback={
              m.role === "assistant" ? (
                <MessageFeedback messageId={m.id} sessionId={sessionId} teamId={teamId} initial={null} />
              ) : null
            }
          />
        ))}

        {turnLoading && (
          <div className="flex items-center gap-2.5 text-white/50 text-sm pl-1">
            <JulietteOrb asAvatar size={26} state="thinking" ariaLabel="Juliette réfléchit" />
            <span className="font-juliette italic text-base">Juliette réfléchit…</span>
          </div>
        )}
        <div ref={scrollEndRef} />
      </div>

      {/* Saisie */}
      <div className="px-4 pb-2 pt-1">
        {/* Aperçu de la capture à envoyer (éphémère) */}
        {image && (
          <div className="mb-2 flex items-center gap-2 pl-1">
            <div className="relative">
              <img
                src={image.dataUrl}
                alt="Capture à envoyer"
                className="h-16 w-16 rounded-lg object-cover border border-white/15"
              />
              <button
                type="button"
                onClick={() => setImage(null)}
                aria-label="Retirer l'image"
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <span className="text-white/40 text-xs">Capture prête à envoyer</span>
          </div>
        )}
        <div className="flex items-end gap-2 rounded-[1.75rem] border border-white/10 bg-white/[0.06] backdrop-blur-xl px-3 py-2 shadow-[0_8px_40px_-12px_rgba(80,70,229,0.5)] focus-within:border-white/25 transition-colors">
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(",")}
            className="hidden"
            onChange={(e) => {
              void handleImageFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={turnLoading}
            aria-label="Joindre une capture d'écran"
            className="shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-white/50 hover:text-white/90 hover:bg-white/10 transition-colors disabled:opacity-40"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <textarea
            ref={taRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            onPaste={(e) => {
              const item = Array.from(e.clipboardData?.items ?? []).find((it) =>
                it.type.startsWith("image/"),
              );
              if (item) {
                e.preventDefault();
                void handleImageFile(item.getAsFile());
              }
            }}
            rows={1}
            placeholder="Écris à Juliette…"
            disabled={turnLoading}
            className="flex-1 resize-none bg-transparent text-[15px] leading-relaxed text-white placeholder:text-white/35 focus:outline-none min-h-[28px] max-h-32 py-1.5"
          />
          <button
            type="button"
            onClick={() => void send()}
            disabled={(!draft.trim() && !image) || turnLoading}
            aria-label="Envoyer"
            className={cn(
              "shrink-0 h-10 w-10 rounded-full flex items-center justify-center transition-all",
              (draft.trim() || image) && !turnLoading
                ? "bg-gradient-to-br from-indigo-400 to-violet-600 text-white shadow-lg shadow-violet-900/40 hover:scale-105"
                : "bg-white/10 text-white/30",
            )}
          >
            {turnLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bulle de message ──────────────────────────────────────────────────────

function Bubble({
  role,
  content,
  feedback,
}: {
  role: string;
  content: string;
  feedback?: ReactNode;
}) {
  const isUser = role === "user";
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[82%] rounded-3xl rounded-br-md bg-gradient-to-br from-indigo-500/90 to-violet-600/90 px-4 py-2.5 text-[15px] leading-relaxed text-white whitespace-pre-wrap break-words shadow-lg shadow-violet-950/30">
          {content}
        </div>
      </div>
    );
  }
  // Deux familles de marqueurs UI peuvent cohabiter dans la meme reponse : on
  // les retire toutes du texte affiche. Avec plusieurs [GOTO:] (mode demo), le
  // bouton de repli vise la DERNIERE route (la destination finale du tour).
  const { text: withoutAddCal, addUrl } = extractAddCal(content);
  const { text, routes } = extractAllGotos(withoutAddCal);
  const route = routes[routes.length - 1];
  return (
    <div className="flex items-start gap-2.5">
      <div className="shrink-0 mt-0.5">
        <JulietteOrb asAvatar size={28} state="idle" ariaLabel="Juliette" />
      </div>
      <div className="flex flex-col items-start gap-1 max-w-[82%]">
        <div className="rounded-3xl rounded-tl-md bg-white/[0.07] border border-white/10 backdrop-blur-sm px-4 py-2.5 text-[15px] leading-relaxed text-white/90 whitespace-pre-wrap break-words">
          {text}
        </div>
        {addUrl && <AddToCalendarButton url={addUrl} />}
        {route && <GoToPageButton route={route} />}
        {feedback}
      </div>
    </div>
  );
}
